import React, { useState, useEffect, useCallback } from 'react';
import bugReportService from '../services/bugReportService';

interface MathCaptchaProps {
  onVerify: (payload: { challengeId: string; answer: number } | null) => void;
  resetKey?: number;
}

const MathCaptcha: React.FC<MathCaptchaProps> = ({ onVerify, resetKey }) => {
  const [challenge, setChallenge] = useState<{ challengeId: string; num1: number; num2: number; operator: string } | null>(null);
  const [answer, setAnswer] = useState<string>('');
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<boolean>(false);

  const calculateAnswer = useCallback((): number | null => {
    if (!challenge) return null;
    switch (challenge.operator) {
      case '+':
        return challenge.num1 + challenge.num2;
      case '-':
        return challenge.num1 - challenge.num2;
      case '*':
        return challenge.num1 * challenge.num2;
      default:
        return null;
    }
  }, [challenge]);

  const fetchChallenge = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    setAnswer('');
    setIsVerified(false);
    onVerify(null);
    try {
      const c = await bugReportService.getCaptcha();
      setChallenge(c);
    } catch (e) {
      setLoadError(true);
      setChallenge(null);
    } finally {
      setLoading(false);
    }
  }, [onVerify]);

  useEffect(() => {
    fetchChallenge();
  }, [fetchChallenge]);

  useEffect(() => {
    // Reset when resetKey changes (and is greater than 0)
    if (resetKey !== undefined && resetKey > 0) {
      fetchChallenge();
    }
  }, [resetKey, fetchChallenge]);

  const handleAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAnswer(value);

    const correctAnswer = calculateAnswer();
    const userAnswer = parseInt(value, 10);

    if (correctAnswer !== null && !isNaN(userAnswer) && userAnswer === correctAnswer && challenge) {
      setIsVerified(true);
      onVerify({ challengeId: challenge.challengeId, answer: userAnswer });
    } else {
      setIsVerified(false);
      onVerify(null);
    }
  };

  const getOperatorSymbol = () => {
    if (!challenge) return '+';
    switch (challenge.operator) {
      case '+':
        return '+';
      case '-':
        return '−';
      case '*':
        return '×';
      default:
        return '+';
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">Loading verification…</div>;
  }

  if (loadError || !challenge) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-red-600">Couldn't load verification.</span>
        <button
          type="button"
          onClick={fetchChallenge}
          className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-2 text-lg font-semibold">
        <span>{challenge.num1}</span>
        <span>{getOperatorSymbol()}</span>
        <span>{challenge.num2}</span>
        <span>=</span>
      </div>
      <input
        type="number"
        value={answer}
        onChange={handleAnswerChange}
        className="w-20 px-2 py-1 border rounded-md dark:bg-deck-ink dark:text-white dark:border-white/10"
        placeholder="?"
        required
      />
      {isVerified && (
        <span className="text-green-600 dark:text-green-400 text-sm">✓</span>
      )}
      <button
        type="button"
        onClick={fetchChallenge}
        className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 underline"
        title="Generate new problem"
      >
        New problem
      </button>
    </div>
  );
};

export default MathCaptcha;